import { FC, useMemo } from 'react';
import * as React from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Dayjs } from 'dayjs';

import { Routes } from 'navigation/routes';
import { AppContainer } from 'components/common/appContainer';
import { useAppStore } from 'core/store/app/useAppStore';
import { ControlledTextInput } from 'components/common/ControlledTextInput';
import { useCreateEvent } from 'hooks/useCreateEvent';
import { EventFormType } from 'types/events/EventFormType';
import { ControlledSelectInput } from 'components/common/ControlledSelectInput';
import { useEventsAdditionalData } from 'hooks/useEventsAdditionalData';
import { UserRoles } from 'core/constants/UserRoles';
import { ControlledAutocompleteInput } from 'components/common/ControlledAutocompleteInput';
import { userFullNameFromUser } from 'utils/userFullNameFromUser';
import { ControlledDateInput } from 'components/common/ControlledDateInput';
import { useEventsPermissions } from 'hooks/useEventsPermissions';
import { useUsers } from 'hooks/useUsers';
import { UserRightsContainer } from 'components/common/UserRightsContainer';
import { TreeViewSelectInput } from 'components/common/TreeViewSelectInput/TreeViewSelectInput';
import { useAdditionalData } from 'hooks/useAdditionalData';
import { ControlledCheckBoxInput } from 'components/common/ControlledCheckBoxInput';
import { RepetitiveEventForm } from '../components/RepetitiveEventForm';

const CreateEvent: FC = () => {
    const navigate = useNavigate();
    const { riskId } = useParams();

    const setErrorAlertText = useAppStore((state) => state.setErrorAlertText);
    const { dictionaryEventTypesInputOptions } = useEventsAdditionalData();
    const { dictionaryRiskSystemInputOptions } = useAdditionalData();
    const {
        canCreateFromResponsibleAppointed,
        canCreateFromUnderRevisionByResponsible,
        canCreateFromApprovedByProjectManager,
    } = useEventsPermissions();
    const { createEvent, createEventWithExtendedFields, permittedUserRolesToCreate } = useCreateEvent(riskId);
    const { users } = useUsers(UserRoles.responsible_for_measure_implementation);

    const expandForm = useMemo(() => {
        return (
            canCreateFromResponsibleAppointed ||
            canCreateFromUnderRevisionByResponsible ||
            canCreateFromApprovedByProjectManager
        );
    }, [canCreateFromResponsibleAppointed, canCreateFromUnderRevisionByResponsible]);

    const {
        control,
        watch,
        handleSubmit,
        setValue,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: {
            eventText: '',
            eventTypeId: '',
            repeatEvent: false,
            repeatUnit: 'WEEK',
            repeatDayOfWeek: 'MONDAY',
            repeatMonth: 1,
            repeatDayOfMonth: 1,
            selectedDates: '',
            systemSubsystemNode: '',
            dueDate: null as Dayjs | null,
            controlDate: null as Dayjs | null,
            eventResponsible: { label: '', value: '' },
        },
        shouldUseNativeValidation: false,
    });

    const watchFields = watch(['repeatEvent', 'repeatUnit']);
    const repeatEvent = watchFields[0];
    const eventRepeatUnit = watchFields[1];

    const responsibleUsers = useMemo(() => {
        return users.map((user) => ({ label: userFullNameFromUser(user), value: user.id }));
    }, [users]);

    const onSubmit: SubmitHandler<EventFormType> = (data) => {
        setErrorAlertText('');
        if (expandForm) {
            createEventWithExtendedFields(data);
        } else {
            createEvent(data);
        }
    };

    const onSave = (): void => {
        if (!isValid) {
            setErrorAlertText('Заполните все поля формы.');
        }
    };

    const onCancel = (): void => {
        if (!riskId) return;
        navigate(`${Routes.RiskEvents}/${riskId}`);
    };

    return (
        <AppContainer>
            <UserRightsContainer allowedUserRoles={permittedUserRolesToCreate}>
                <div className="container_thin">
                    <h2>Создание нового мероприятия</h2>
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={2} mb={'32px'}>
                            <ControlledSelectInput
                                control={control}
                                label={'Тип мероприятия'}
                                name={'eventTypeId'}
                                error={!!errors.eventTypeId}
                                options={dictionaryEventTypesInputOptions}
                                required
                            />
                            <ControlledTextInput
                                name={'eventText'}
                                control={control}
                                label={'Текст мероприятия'}
                                error={!!errors.eventText}
                                required
                            />
                            <TreeViewSelectInput
                                control={control}
                                values={dictionaryRiskSystemInputOptions}
                                label={'Система/подсистема/узел'}
                                name={'systemSubsystemNode'}
                                error={!!errors.systemSubsystemNode}
                                required
                            />
                            {expandForm && (
                                <>
                                    <ControlledAutocompleteInput
                                        control={control}
                                        label={'Ответственный за исполнение мероприятия'}
                                        name={'eventResponsible'}
                                        error={!!errors.eventResponsible}
                                        options={responsibleUsers}
                                        required
                                    />
                                    <ControlledDateInput
                                        control={control}
                                        label={'Срок выполнения (плановая дата)'}
                                        name={'dueDate'}
                                        error={!!errors.dueDate}
                                        required
                                    />
                                    <ControlledDateInput
                                        control={control}
                                        label={'Срок контроля мероприятия'}
                                        name={'controlDate'}
                                        error={!!errors.controlDate}
                                        required
                                    />
                                </>
                            )}
                            <ControlledCheckBoxInput
                                name={'repeatEvent'}
                                control={control}
                                label={'Повторять мероприятие'}
                            />
                            {repeatEvent && (
                                <RepetitiveEventForm
                                    defaultValue={''}
                                    eventRepeatUnit={eventRepeatUnit}
                                    repeatEvent={repeatEvent}
                                    control={control}
                                    setValue={setValue}
                                />
                            )}
                            <div style={{ display: 'flex' }}>
                                <Box mr={'10px'}>
                                    <Button variant="contained" type={'submit'} onClick={onSave}>
                                        Создать мероприятие
                                    </Button>
                                </Box>
                                <Box mr={'10px'}>
                                    <Button variant="outlined" onClick={onCancel}>
                                        Назад
                                    </Button>
                                </Box>
                            </div>
                        </Stack>
                    </form>
                </div>
            </UserRightsContainer>
        </AppContainer>
    );
};

export { CreateEvent };
